import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeTab) || categories[0];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.jiujiujiu.com', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': `https://www.jiujiujiu.com/category/${currentCategory.slug}`, name: currentCategory.name } },
    ],
  };

  const webpageSchema = {
    name: `${currentCategory.name} - 久久久品质分类`,
    description: currentCategory.description,
    url: `https://www.jiujiujiu.com/category/${currentCategory.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={webpageSchema} />

      <Navbar />

      <main className="pt-16">
        <section className="relative h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src={currentCategory.image}
            alt={currentCategory.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/35"></div>
          <div className="relative z-10 text-center px-4 max-w-[700px] mx-auto">
            <p className="text-white/75 text-sm mb-3">久久久品质分类</p>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-white mb-4">{currentCategory.name}</h1>
            <p className="text-white/70 text-sm md:text-base leading-relaxed">{currentCategory.description}</p>
          </div>
        </section>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '产品分类', path: '/category/home-living' },
              { label: currentCategory.name },
            ]}
          />

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveTab(cat.slug)}
                className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 pb-16 md:pb-20">
            {currentCategory.products.map((product) => (
              <div
                key={product.id}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden bg-background-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{product.name}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">{product.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-primary-500">{product.price}</span>
                    <span className="text-xs text-foreground-400">起</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {currentCategory.products.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无产品，敬请期待</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}